import {
  Box,
  Flex,
  ReportSection,
  ReportSectionItemContent,
} from '@/components';
import type { LedgerInspectionAndImprovementSectionProps } from '@/features/mold/models/ledger';
import {
  ActionOfReflectionStatusLabelModifier,
  DiscriminatedInspectionEvaluationModel,
  InspectionByExternalExpertReasonModifier,
  OpinionByAdministrativeReviewTeamLabelModifier,
  ProcessTypeLabelModifier,
} from '@/features/project/projects/_models/project';
import { useLocaledAmount } from '@/providers';
import { AmountUnitLabelModifier, formatValueByUnit } from '@/utils';
import { ReviewTypeLabelModifier } from '@/utils/enum';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LEDGER_BORDER,
  LEDGER_RIGHT_ALIGNED_STYLE,
  type TableRowType,
  getEmptyTableDataByHeader,
} from '../../../utils';
import { TableContent } from './TableContent';

export const InspectionAndImprovementSection: FC<
  LedgerInspectionAndImprovementSectionProps
> = ({ project, accountTitleOptions, accountingTypes }) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const parsedInspectionEvaluation =
    DiscriminatedInspectionEvaluationModel.safeParse(project);
  const inspectionEvaluation = parsedInspectionEvaluation.success
    ? parsedInspectionEvaluation.data.sheetType === 'RS'
      ? parsedInspectionEvaluation.data.inspectionEvaluation
      : undefined
    : undefined;

  const reflectedAmountTableData = useMemo<TableRowType>(() => {
    const header = {
      isHeader: true,
      row: [
        { text: t('accounting-name'), cellStyle: { borderLeft: 0 } },
        { text: t('account-title') },
        {
          textWrapperStyle: { justifyContent: 'center' },
          text: t('reflected-amount-with-unit', {
            unit: AmountUnitLabelModifier[unit],
          }),
        },
      ],
    };

    const reflectedAmountOfGeneralAccount =
      inspectionEvaluation?.reflectionStatusBasedOnOpinion
        ?.reflectedAmountOfGeneralAccount;

    const data = [
      ...(reflectedAmountOfGeneralAccount
        ? [
            {
              accountingTypeId: accountingTypes?.find(
                (accountingType) => accountingType.type === 'general',
              )?.id,
              accountTitleId: null,
              reflectedAmount: reflectedAmountOfGeneralAccount,
            },
          ]
        : []),
      ...(inspectionEvaluation?.reflectionStatusBasedOnOpinion
        ?.specialAccount ?? []),
    ].map<TableRowType[number]>((v) => ({
      row: [
        {
          cellStyle: { borderLeft: 0 },
          text: accountingTypes?.find(
            (accountingType) => accountingType.id === v.accountingTypeId,
          )?.name,
        },
        {
          text: accountTitleOptions?.find(
            (accountTitle) => accountTitle.value === v.accountTitleId,
          )?.label,
        },
        {
          textWrapperStyle: LEDGER_RIGHT_ALIGNED_STYLE,
          text:
            typeof v.reflectedAmount === 'number'
              ? formatValueByUnit(v.reflectedAmount, {
                  operation: 'round',
                  unit,
                })
              : null,
        },
      ],
    }));

    return data.length ? [header, ...data] : getEmptyTableDataByHeader(header);
  }, [
    accountTitleOptions,
    accountingTypes,
    inspectionEvaluation?.reflectionStatusBasedOnOpinion
      ?.reflectedAmountOfGeneralAccount,
    inspectionEvaluation?.reflectionStatusBasedOnOpinion?.specialAccount,
    t,
    unit,
  ]);

  // Match the end position of the accounting-name in the reflected-amount-table
  const positionRate = { flex: 1.34, borderLeft: LEDGER_BORDER };

  return (
    <ReportSection
      title={t('inspection-evaluation')}
      items={[
        {
          subtitle: t(
            'inspection-and-improvement-by-administrative-jurisdiction',
          ),
          item: (
            <div>
              <ReportSectionItemContent
                label={t('inspection-result')}
                value={
                  inspectionEvaluation
                    ?.inspectionAndImprovementByAdministrativeJurisdiction
                    ?.inspectionResult
                }
              />
              <ReportSectionItemContent
                label={t('evaluation')}
                value={
                  inspectionEvaluation
                    ?.inspectionAndImprovementByAdministrativeJurisdiction
                    ?.evaluation
                }
              />
              <ReportSectionItemContent
                label={t('direction-of-improvement')}
                value={
                  inspectionEvaluation
                    ?.inspectionAndImprovementByAdministrativeJurisdiction
                    ?.directionOfImprovement
                }
                isLast
              />
            </div>
          ),
        },
        {
          subtitle: t('inspection-by-external-expert'),
          item: (
            <Box>
              <Flex>
                <ReportSectionItemContent
                  label={t('inspection-target')}
                  value={
                    inspectionEvaluation?.inspectionByExternalExpert
                      ?.reviewType &&
                    ReviewTypeLabelModifier[
                      inspectionEvaluation.inspectionByExternalExpert.reviewType
                    ]
                  }
                />
                <ReportSectionItemContent
                  wrapperStyle={positionRate}
                  label={t('last-implemented-fiscal-year')}
                  value={
                    inspectionEvaluation?.inspectionByExternalExpert
                      ?.lastImplementedFiscalYear
                  }
                />
              </Flex>
              <ReportSectionItemContent
                label={t('reason-of-target')}
                value={
                  inspectionEvaluation?.inspectionByExternalExpert?.reason &&
                  InspectionByExternalExpertReasonModifier[
                    inspectionEvaluation.inspectionByExternalExpert.reason
                  ]
                }
              />
              <ReportSectionItemContent
                label={t('opinion')}
                value={
                  inspectionEvaluation?.inspectionByExternalExpert?.opinion
                }
              />
              <ReportSectionItemContent
                label={t('result-of-publishing')}
                value={
                  inspectionEvaluation?.inspectionByExternalExpert
                    ?.resultOfPublishing
                }
                isLast
              />
            </Box>
          ),
        },
        {
          subtitle: t('process-and-opinion-by-administrative-review-team'),
          isLongText: true,
          item: (
            <>
              <ReportSectionItemContent
                label={t('opinion')}
                hasWrapperFlex={false}
                value={
                  inspectionEvaluation
                    ?.processAndOpinionByAdministrativeReviewTeam?.opinion &&
                  OpinionByAdministrativeReviewTeamLabelModifier[
                    inspectionEvaluation
                      .processAndOpinionByAdministrativeReviewTeam.opinion
                  ]
                }
                isLast
              />
              <ReportSectionItemContent
                label={t('details')}
                wrapperStyle={positionRate}
                value={
                  inspectionEvaluation
                    ?.processAndOpinionByAdministrativeReviewTeam?.context
                }
                hasLeftBorder
                isLast
              />
            </>
          ),
        },
        {
          subtitle: t('reflection-status-based-on-opinion'),
          item: (
            <Box>
              <ReportSectionItemContent
                label={t('improvement-and-reflection-status')}
                value={
                  inspectionEvaluation?.reflectionStatusBasedOnOpinion
                    ?.action &&
                  ActionOfReflectionStatusLabelModifier[
                    inspectionEvaluation.reflectionStatusBasedOnOpinion.action
                  ]
                }
              />
              <ReportSectionItemContent
                label={t('reflected-amount')}
                value={
                  <TableContent
                    wrapperStyle={{ width: '100%' }}
                    data={reflectedAmountTableData}
                  />
                }
              />
              <ReportSectionItemContent
                label={t('details')}
                value={
                  inspectionEvaluation?.reflectionStatusBasedOnOpinion?.context
                }
                isLast
              />
            </Box>
          ),
        },
        {
          subtitle: t('summary-of-publishing'),
          item: inspectionEvaluation?.summariesAndResponses?.length ? (
            <Box>
              {inspectionEvaluation.summariesAndResponses.map(
                (summary, idx, arr) => {
                  const isLastElement = idx === arr.length - 1;
                  return (
                    // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                    <Box key={idx}>
                      <Flex>
                        <ReportSectionItemContent
                          label={t('summary-process')}
                          value={
                            summary.process &&
                            ProcessTypeLabelModifier[summary.process]
                          }
                        />
                        <ReportSectionItemContent
                          label={t('summary-fiscal-year')}
                          value={summary.fiscalYear}
                          hasLeftBorder
                        />
                      </Flex>
                      <ReportSectionItemContent
                        wrapperStyle={{
                          flexDirection: 'column',
                        }}
                        labelStyle={{
                          width: '100%',
                          borderRight: 'none',
                          borderLeft: 'none',
                          borderBottom: LEDGER_BORDER,
                        }}
                        label={t('summary-content')}
                        value={summary.content}
                      />
                      <ReportSectionItemContent
                        wrapperStyle={{
                          borderBottom: isLastElement ? 'none' : LEDGER_BORDER,
                          flexDirection: 'column',
                        }}
                        labelStyle={{
                          width: '100%',
                          borderRight: 'none',
                          borderLeft: 'none',
                          borderBottom: LEDGER_BORDER,
                        }}
                        label={t('response-status')}
                        value={summary.response}
                      />
                    </Box>
                  );
                },
              )}
            </Box>
          ) : (
            <ReportSectionItemContent
              wrapperStyle={{
                height: '100%',
                borderBottom: 'none',
              }}
              value={null}
            />
          ),
        },
        {
          subtitle: t('others-indicated-item'),
          item: inspectionEvaluation?.otherIndicatedItemsAndResponses
            ?.length ? (
            <Box>
              {inspectionEvaluation.otherIndicatedItemsAndResponses.map(
                (item, idx, arr) => {
                  const isLastElement = idx === arr.length - 1;
                  return (
                    // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                    <Box key={idx}>
                      <Flex>
                        <ReportSectionItemContent
                          labelStyle={{ width: 170 }}
                          label={t('indicated-reason')}
                          value={item.reason}
                        />
                        <ReportSectionItemContent
                          label={t('indicated-fiscal-year')}
                          value={item.fiscalYear}
                          wrapperStyle={{ borderLeft: LEDGER_BORDER }}
                        />
                      </Flex>
                      <ReportSectionItemContent
                        wrapperStyle={{
                          flexDirection: 'column',
                        }}
                        labelStyle={{
                          width: '100%',
                          borderRight: 'none',
                          borderLeft: 'none',
                          borderBottom: LEDGER_BORDER,
                        }}
                        label={t('indicated-content')}
                        value={item.content}
                      />
                      <ReportSectionItemContent
                        wrapperStyle={{
                          borderBottom: isLastElement ? 'none' : LEDGER_BORDER,
                          flexDirection: 'column',
                        }}
                        labelStyle={{
                          width: '100%',
                          borderRight: 'none',
                          borderLeft: 'none',
                          borderBottom: LEDGER_BORDER,
                        }}
                        label={t('response-status')}
                        value={item.response}
                      />
                    </Box>
                  );
                },
              )}
            </Box>
          ) : (
            <ReportSectionItemContent
              wrapperStyle={{
                height: '100%',
                borderBottom: 'none',
              }}
              value={null}
            />
          ),
        },
      ]}
    />
  );
};
